import * as React from "react";
import { useSelector } from "react-redux";
import { Dispatch } from "redux";

import { Activity, ActivityActions } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { ApplicationActions } from "@com.mgmtp.a12.client/client-core/lib/core/application";
import { Model, ModelSelectors } from "@com.mgmtp.a12.client/client-core/lib/core/model";
import { JSONDocument } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/models";
import { ContextMenu } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/table/sub-components/context-menu";
import { RightClickContextMenu } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/table/sub-components/right-click-context-menu";
import { LoggerFactory } from "@com.mgmtp.a12.utils/utils-logging";
import { List, PopUpMenu } from "@com.mgmtp.a12.widgets/widgets-core";
import { Icon } from "@com.mgmtp.a12.widgets/widgets-core/lib/icon";

import { createCustomNotificationAction } from "../../../app/actions";
import { FachverfahrenManifest } from "../../../app/slices/fachverfahren/types";
import { GenericObject, getDataOfObjectAtPath } from "../../../helpers/objectHelper";
import { RESOURCE_KEYS } from "../../../localization";
import { cleanAppIdPrefix } from "../../../utilities/applicationKeyUtils";
import { getValueFromModelPath } from "../../../utilities/pathHelpers";
import { hasStartProcessPermission } from "../../../utilities/permissionUtils";
import { modulfModulfCopyFachverfahrenData } from "../../../utilities/rpcHelper";
import { isString } from "../../../utilities/typeGuardUtils";
import { isAdmin, isAllowedRuntimeModule, isUser, selectPermissionsForFv } from "../../../utilities/user";
import { PATH_TO_ERSTELLT_IN_VERSION, PATH_TO_STATUS } from "../../../utilities/variables/documentModelPaths";
import {
	getProcessStatesConfigValue,
	RUNTIME_CONFIG_VALUES,
	RUNTIME_MODULES
} from "../../../utilities/variables/RuntimeModules";
import { ProzessschrittStatus } from "../../processFormEngine/sagas/constants";
import { isDocumentWithFachverfahrenData, isDocumentWithProcessState } from "../../workflows/data/fachverfahrenData";
import { isDocumentWithProcessMetadata, ProcessMetadataContainingDocument } from "../../workflows/data/processMetadata";

import { createNewAntrag, ModulfUser } from "./common";

const logger = LoggerFactory.getLogger("modulf");

const ERSTELLT_IN_BACKGROUND_STYLE = { backgroundColor: "#eaecee" };

export function CustomContextMenu(
	dispatch: Dispatch,
	user: ModulfUser,
	activityId: string,
	documents: ProcessMetadataContainingDocument[],
	fachverfahrenManifest: FachverfahrenManifest
): (props: ContextMenu.Props | RightClickContextMenu.Props) => React.ReactElement {
	return function CustomContextMenu(props: ContextMenu.Props | RightClickContextMenu.Props): React.ReactElement {
		// be aware that we will get the cross version model here which is fine here because it is
		// always contains all fields from the latest fachverfahren version model (cross model => latest version model)
		// if the behavior changes then we need to get the right model from dataservices
		const documentModel = useSelector(
			ModelSelectors.modelInScene({ activityId, modelType: "document" }, Model.isDocumentModel)
		);

		const { createdAutomatically = false, fachverfahren: { client = "", key = "" } = {} } =
			isDocumentWithProcessMetadata(props.row) ? props.row.root.processMetadata : {};

		const handleDeleteConfirm = React.useCallback(() => {
			dispatch(
				ActivityActions.create({
					initiatingActivityId: activityId,
					loadingState: "without",
					activityDescriptor: {
						modal: "DeleteProcessConfirmModal",
						instance: props.row.id,
						createdAutomatically: createdAutomatically ? "true" : "false"
					}
				})
			);
		}, [createdAutomatically, props.row.id]);

		const onMembershipRowButtonClick = React.useCallback(() => {
			dispatch(
				ActivityActions.create({
					initiatingActivityId: activityId,
					loadingState: "without",
					activityDescriptor: {
						modal: "ChangeMembershipModal",
						instance: props.row.id,
						client,
						key
					}
				})
			);
		}, [createdAutomatically, props.row.id]);

		const onDuplicateRowButtonClick = React.useCallback(() => {
			dispatch(ApplicationActions.setBusy(true));
			const document = documents.find(e => e.id === props.row.id);
			if (!document || !documentModel) {
				showDuplicateAntragError(dispatch, activityId, props.row.id);
				return;
			}

			modulfModulfCopyFachverfahrenData(document.id)
				.then(response => {
					const copiedDocument = response.document;
					if (!copiedDocument || !isDocumentWithFachverfahrenData(copiedDocument)) {
						throw new Error(`Could not find valid document with id ${document.id}`);
					}

					createNewAntrag(
						dispatch,
						document.root.processMetadata.fachverfahren.client ?? "",
						fachverfahrenManifest,
						activityId,
						copiedDocument
					);
					if (response.cleanedModelFields?.length) {
						const removedFields = response.cleanedModelFields.map(it => it.prettyDisplayPath);
						dispatch(
							createCustomNotificationAction({
								activityId,
								severity: "warning",
								title: { key: RESOURCE_KEYS.features.notification.title.warning },
								message: {
									key: RESOURCE_KEYS.features.notification.warning.duplicateProcess.removedFields
								},
								componentId: "NotificationWithList",
								items: removedFields,
								group: `duplicateAntrag:${props.row.id}`
							})
						);
					}
				})
				.catch(e => {
					logger.error(e);
					showDuplicateAntragError(dispatch, activityId, props.row.id);
				});
		}, [props.row.id, documents, user, fachverfahrenManifest, activityId, dispatch, documentModel]);

		const version = getDataOfObjectAtPath(
			PATH_TO_ERSTELLT_IN_VERSION.map(entry => entry.elementName),
			props.row as GenericObject
		);
		const erstelltInText = `Erstellt mit Version ${isString(version) ? version : "Unbekannt"}`;

		const deleteAllowed = canProcessBeDeleted(user, fachverfahrenManifest, props.row);

		const userFvPermissions = useSelector(selectPermissionsForFv(fachverfahrenManifest.identification.key));
		const hasAdminRole = isUser(user) ? isAdmin(user) : false;
		const duplicateAllowed = hasStartProcessPermission(userFvPermissions) || hasAdminRole;

		const changeMembershipAllowed =
			isInErfassung(props.row) &&
			(hasStartProcessPermission(userFvPermissions) || hasAdminRole) &&
			(fachverfahrenManifest.clients?.length ?? 0) > 1;

		const listElement = (
			<List>
				<List.Item
					style={ERSTELLT_IN_BACKGROUND_STYLE}
					readonly
					text={erstelltInText}
					graphic={<Icon iconTheme="outlined">info</Icon>}
				/>
				{deleteAllowed && (
					<List.Item
						text="Löschen"
						onClick={handleDeleteConfirm}
						graphic={<Icon iconTheme="outlined">delete</Icon>}
					/>
				)}
				{duplicateAllowed && (
					<List.Item
						text="Vorgang duplizieren"
						graphic={<Icon iconTheme="filled">content_copy</Icon>}
						onClick={onDuplicateRowButtonClick}
					/>
				)}
				{changeMembershipAllowed && (
					<List.Item
						text="Zugehörigkeit ändern"
						graphic={<Icon iconTheme="filled">diversity_3</Icon>}
						onClick={onMembershipRowButtonClick}
					/>
				)}
			</List>
		);

		const onTriggerElementClick = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
		}, []);

		if ("contextMenuModel" in props && "row" in props) {
			return <PopUpMenu onTriggerElementClick={onTriggerElementClick}>{listElement}</PopUpMenu>;
		}

		return listElement;
	};
}

function isInErfassung(document: JSONDocument): boolean {
	const prevProzessschritteStatus = getValueFromModelPath(PATH_TO_STATUS, document as Activity.Data.Document);
	return prevProzessschritteStatus === ProzessschrittStatus.Neu;
}

function canProcessBeDeleted(
	user: ModulfUser,
	fachverfahrenManifest: FachverfahrenManifest,
	document: JSONDocument
): boolean {
	const createdAutomatically = isDocumentWithProcessMetadata(document)
		? (document.root.processMetadata.createdAutomatically ?? false)
		: true;

	return deleteAllowed(
		user,
		fachverfahrenManifest,
		document,
		...(createdAutomatically
			? ([
					RUNTIME_MODULES.DELETE_AUTOMATIC_PROCESS,
					RUNTIME_CONFIG_VALUES.DELETE_AUTOMATIC_PROCESS_STATES
				] as const)
			: ([RUNTIME_MODULES.DELETE_MANUAL_PROCESS, RUNTIME_CONFIG_VALUES.DELETE_MANUAL_PROCESS_STATES] as const))
	);
}

function deleteAllowed(
	user: ModulfUser,
	fachverfahrenManifest: FachverfahrenManifest,
	document: JSONDocument,
	runtimeModuleName: string,
	roleSpec: string
): boolean {
	if (isUser(user) && isAdmin(user)) {
		return true;
	}

	if (
		!isAllowedRuntimeModule(user, runtimeModuleName, fachverfahrenManifest) ||
		!isDocumentWithProcessState(document)
	) {
		return false;
	}

	const deleteRolesConfig = fachverfahrenManifest.configurationValues.find(it => it.key === roleSpec);
	const allowedProcessStates = deleteRolesConfig ? getProcessStatesConfigValue(deleteRolesConfig) : [];
	const currentStatus = cleanAppIdPrefix(document.root.processMetadata.task.key);

	return allowedProcessStates.map(cleanAppIdPrefix).includes(currentStatus);
}

function showDuplicateAntragError(dispatch: Dispatch, activityId: string, id: string): void {
	dispatch(
		createCustomNotificationAction({
			title: { key: RESOURCE_KEYS.features.notification.title.error },
			message: { key: RESOURCE_KEYS.features.notification.error.duplicateProcess },
			icon: "error",
			severity: "error",
			activityId,
			group: `duplicateAntrag:${id}`
		})
	);
}
